// ════════════════════════════════════════════════════════════════════
// JGA SAFETY INFRASTRUCTURE — Error reporter + storage wrappers
// ════════════════════════════════════════════════════════════════════
// Extracted from App.jsx Jun 19, 2026 as part of Phase B Step 1
// (Stamps extraction). Lives here so any module can `import` these
// helpers without depending on App.jsx.
//
// JGAErrorBoundary (the React class component) stays in App.jsx
// because it's only used at the App root — no need to share it.
// ════════════════════════════════════════════════════════════════════


// ── Global error reporter ──────────────────────────────────────────
// Replaces silent catches with structured logging. Today it logs to
// console + an in-memory buffer accessible via window.__jgaErrors for
// debugging. Tomorrow we wire it to a backend endpoint by replacing
// just this function — every call site is already correct. Context is
// a free-form string identifying what was happening when the error
// fired ("boards.apply", "stamps.save", "itinerary.parse", etc).
//
// Convention: call as jgaErrorReport(error, "module.action", { extras }).
// The extras object is preserved for backend forwarding and is shown
// in console.
export function jgaErrorReport(error, context = "unknown", extras = {}) {
  const entry = {
    ts: new Date().toISOString(),
    context,
    message: error?.message || String(error),
    stack: error?.stack || null,
    name: error?.name || "Error",
    extras,
    url: typeof window !== "undefined" ? window.location.pathname : null,
  };
  // Always log to console — devs see it immediately in DevTools.
  try { console.error(`[JGA ERROR] ${context}:`, error, extras); } catch (e) {}
  // In-memory buffer, capped at 100 most recent so it doesn't grow
  // unbounded on long sessions.
  if (typeof window !== "undefined") {
    if (!window.__jgaErrors) window.__jgaErrors = [];
    window.__jgaErrors.push(entry);
    if (window.__jgaErrors.length > 100) window.__jgaErrors.shift();
  }
  // Hook for future backend wiring. When the errors endpoint exists,
  // POST entry here. Wrapping in try/catch so a logging failure
  // never crashes the caller — that would defeat the whole point.
  try {
    if (typeof window !== "undefined" && typeof window.jgaErrorEndpoint === "function") {
      window.jgaErrorEndpoint(entry);
    }
  } catch (e) {}
}

// Expose globally so non-component code (event handlers, async
// callbacks, code that can't easily import) can call without an import.
// Module side-effect — runs once when the file is first loaded.
if (typeof window !== "undefined") {
  window.jgaErrorReport = jgaErrorReport;
}


// ── safeStorage — localStorage wrappers ────────────────────────────
// Two problems we're solving:
// 1. Silent quota errors (Safari private mode, full disk, etc) —
//    writes appear to succeed but actually fail.
// 2. JSON.parse failures on corrupted entries left by old app
//    versions — readers crash where they should fall back.
// Both surface via jgaErrorReport now. Callers get a clear bool back
// from setItem so they can decide what to do (toast the user, queue
// for retry, etc).
export const safeStorage = {
  // Returns true on success, false on failure. Quota errors are
  // detected and reported with a distinct context so we can tell
  // them apart from generic write failures in telemetry.
  setItem(key, value) {
    if (typeof window === "undefined" || !window.localStorage) return false;
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (e) {
      const isQuota = e?.name === "QuotaExceededError" ||
                      e?.code === 22 ||
                      e?.code === 1014; // Firefox quota code
      jgaErrorReport(e, isQuota ? "storage.quotaExceeded" : "storage.setItem.failed", { key });
      return false;
    }
  },
  // Returns the raw string or null. Never throws.
  getItem(key) {
    if (typeof window === "undefined" || !window.localStorage) return null;
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      jgaErrorReport(e, "storage.getItem.failed", { key });
      return null;
    }
  },
  // Convenience: get + JSON.parse with a default fallback. Used
  // wherever the existing code does try { JSON.parse(getItem(...)) }
  // catch (e) {} — same shape but errors get reported.
  getJSON(key, fallback = null) {
    const raw = this.getItem(key);
    if (raw == null) return fallback;
    try {
      return JSON.parse(raw);
    } catch (e) {
      jgaErrorReport(e, "storage.getJSON.parse", { key });
      return fallback;
    }
  },
  // Convenience: stringify + set in one call.
  setJSON(key, value) {
    try {
      return this.setItem(key, JSON.stringify(value));
    } catch (e) {
      jgaErrorReport(e, "storage.setJSON.stringify", { key });
      return false;
    }
  },
  removeItem(key) {
    if (typeof window === "undefined" || !window.localStorage) return false;
    try {
      window.localStorage.removeItem(key);
      return true;
    } catch (e) {
      jgaErrorReport(e, "storage.removeItem.failed", { key });
      return false;
    }
  },
};

// Expose globally for non-module code.
if (typeof window !== "undefined") {
  window.jgaStorage = safeStorage;
}
// Default export — for bundlers (like CRA's Webpack) that need explicit
// default exports to recognize a file as a proper ES module. Named
// exports above remain the primary API; this is purely a compatibility
// shim. Added Jun 19, 2026 to fix Vercel CRA build error.
export default { jgaErrorReport, safeStorage };
